/**
 * Header behaviour.
 * <site-header>: toggles [data-scrolled] for the soft shadow, and keeps the
 *   desktop <details> dropdowns tidy (one open at a time, Esc + outside click).
 * <menu-drawer> / <search-modal>: modal <dialog> with explicit focus trap,
 *   Esc to close, backdrop click, aria-expanded sync and exit animation.
 */
(() => {
if (!customElements.get('site-header')) {
  customElements.define(
    'site-header',
    class SiteHeader extends HTMLElement {
      connectedCallback() {
        this.onScroll = this.onScroll.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        document.addEventListener('click', this.onDocumentClick);
        this.update();

        this.dropdowns = Array.from(this.querySelectorAll('.header__dropdown'));
        this.dropdowns.forEach((details) => {
          details.addEventListener('toggle', () => {
            if (!details.open) return;
            this.dropdowns.forEach((other) => {
              if (other !== details) other.open = false;
            });
          });
          details.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape' || !details.open) return;
            details.open = false;
            details.querySelector('summary').focus();
          });
        });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('click', this.onDocumentClick);
      }

      onDocumentClick(event) {
        (this.dropdowns || []).forEach((details) => {
          if (details.open && !details.contains(event.target)) details.open = false;
        });
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      }

      update() {
        this.toggleAttribute('data-scrolled', window.scrollY > 8);
      }
    }
  );
}

class DialogTrigger extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.opener = this.querySelector('[data-dialog-open]');
    if (!this.dialog || !this.opener) return;

    this.opener.addEventListener('click', () => this.open());
    this.querySelectorAll('[data-dialog-close]').forEach((button) =>
      button.addEventListener('click', () => this.close())
    );
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));
    this.dialog.addEventListener('close', () => {
      this.dialog.classList.remove('is-closing');
      this.opener.setAttribute('aria-expanded', 'false');
      this.opener.focus();
    });
  }

  focusables() {
    return Array.from(
      this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), summary, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const items = this.focusables();
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  open() {
    if (this.dialog.open) return;
    this.dialog.showModal();
    this.opener.setAttribute('aria-expanded', 'true');
    this.afterOpen();
  }

  afterOpen() {}

  close() {
    if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return this.dialog.close();
    this.dialog.classList.add('is-closing');
    const panel = this.dialog.firstElementChild;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      this.dialog.close();
    };
    panel.addEventListener('animationend', finish, { once: true });
    setTimeout(finish, 400);
  }
}

if (!customElements.get('menu-drawer')) {
  customElements.define(
    'menu-drawer',
    class MenuDrawer extends DialogTrigger {
      connectedCallback() {
        super.connectedCallback();
        if (!this.dialog) return;
        this.dialog.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => this.close()));
        window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
          if (event.matches && this.dialog.open) this.dialog.close();
        });
      }
    }
  );
}

if (!customElements.get('search-modal')) {
  customElements.define(
    'search-modal',
    class SearchModal extends DialogTrigger {
      afterOpen() {
        const input = this.dialog.querySelector('input[type="search"]');
        if (input) input.focus();
      }
    }
  );
}
})();
